import type { StaticSearch } from "~/classes/search/StaticSearch"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId } from "~/classes/entity"
import { StaticFile } from "~/classes/file/StaticFile"

class ColumnView extends GlobalComposable<{
  currentFolderId?: string
  currentSearchId?: string
  lastOpenedFolderId?: string
}> {
  override defaultState = () => ({
    lastOpenedFolderId: undefined,
  })

  addListeners = () => {
    const { addEntityEventListener } = useEvents()

    const folderId = this.state.value.currentFolderId
    if (folderId) {
      addEntityEventListener("childCreated", folderId, this.onChildCreated)
      addEntityEventListener("childProperties", folderId, this.onChildProperties)
      addEntityEventListener("childDeleted", folderId, this.onChildDeleted)
      addEntityEventListener("childRemoved", folderId, this.onChildRemoved)
    }
    const searchId = this.state.value.currentSearchId
    if (searchId) {
      addEntityEventListener("searchResult", searchId, this.onSearchResult)
    }
  }

  clearListeners = () => {
    const { removeEntityEventListener } = useEvents()
    const folderId = this.state.value.currentFolderId
    const searchId = this.state.value.currentSearchId
    if (folderId) {
      removeEntityEventListener("childCreated", folderId, this.onChildCreated)
      removeEntityEventListener("childProperties", folderId, this.onChildProperties)
      removeEntityEventListener("childDeleted", folderId, this.onChildDeleted)
      removeEntityEventListener("childRemoved", folderId, this.onChildRemoved)
    }
    if (searchId) {
      removeEntityEventListener("searchResult", searchId, this.onSearchResult)
    }
  }

  /** clears the main view state, the current contents, folder */
  clearStateAndListeners = () => {
    const { state } = useCollectionView()

    this.clearListeners()

    for (const fileId in state.value.contents)
      this.removeFileFromView(fileId)

    if (len(state.value.contentBounds))
      this.warn("Content bounds not empty", state.value.contentBounds)
    if (len(state.value.contents))
      this.warn("Contents not empty", state.value.contents)

    state.value.contents = {}
    state.value.contentBounds = {}
    this.state.value.lastOpenedFolderId = undefined
  }

  addFileIfNew = (file: StaticFile) => {
    const { currentRootFolder, currentOpenFolders } = useNavigation()
    const { state } = useCollectionView()

    // We update our contents with the file, ensuring that reactivity is triggered if there
    // were changes.
    if (state.value.contents[file.id]) {
      const existingFile = state.value.contents[file.id]
      if (existingFile.mergeWithNewer(file))
        state.value.contents[file.id] = markRaw(new StaticFile(existingFile.inner()))
      return
    }

    // additional checks for if we are viewing a folder
    if (isDefined(currentRootFolder.value)) {
      const parentId = file.inferredParentId()
      if (!parentId) {
        return this.error("Added file doesn't have a parent id, skipping", file)
      }
      if (!currentOpenFolders.value[parentId]) {
        return this.error("Added file doesn't match current open folder", file)
      }
    }

    if (!file.mimeType()) {
      return this.warn("Skipping adding file as it lacks a mime type. This can come from spurious updates or race conditions")
    }

    state.value.contents[file.id] = markRaw(file)
  }

  removeFileFromView = (fileId: string) => {
    const { setCurrentNavigationState, state: navigationState, currentOpenFolders } = useNavigation()
    const { state } = useCollectionView()
    delete state.value.contents[fileId]
    delete state.value.contentBounds[fileId]

    const openFolders = currentOpenFolders.value
    if (openFolders[fileId] && navigationState.value.current?.navType === "collection-folder") {
      delete openFolders[fileId]
      setCurrentNavigationState({ ...navigationState.value.current, openFolders })
    }
  }

  onNavigatedToFolder = (folder: StaticFile) => {
    const { listMultipleFoldersWithViewModeProperties } = useFileSystem()
    const { addEntityEventListener } = useEvents()
    const { isOffline } = useCache()
    const { setCurrentNavigationState, state: navigationState, currentOpenFolders } = useNavigation()

    if (navigationState.value.current?.navType !== "collection-folder")
      return this.warn("Navigation state unexpected, was this a refresh?")

    this.clearStateAndListeners()
    this.state.value.currentFolderId = folder.id
    this.state.value.currentSearchId = undefined

    // Keep any state added by our navigation router, since that might have some
    // cached folders from our history back/forward state
    setCurrentNavigationState({
      ...navigationState.value.current,
      openFolders: { ...currentOpenFolders.value, [folder.id]: true },
    })

    const folderIds = Object.keys(currentOpenFolders.value)
    for (const folderId of folderIds) {
      addEntityEventListener("childCreated", folderId, this.onChildCreated)
      addEntityEventListener("childProperties", folderId, this.onChildProperties)
      addEntityEventListener("childDeleted", folderId, this.onChildDeleted)
      addEntityEventListener("childRemoved", folderId, this.onChildRemoved)
    }
    listMultipleFoldersWithViewModeProperties(folderIds, 50, isOffline())
  }

  onNavigatedToSearch = (search: StaticSearch) => {
    const { listSearch } = useSearch()
    const { isOffline } = useCache()
    const { currentSearchIsFresh } = useNavigation()

    this.clearStateAndListeners()
    this.state.value.currentFolderId = undefined
    this.state.value.currentSearchId = search.id
    this.addListeners()

    // if the search is fresh, we don't need to list it as it was already
    // listed. This is a bit messy, perhaps we can clean up the logic
    // somewhere
    if (currentSearchIsFresh.value)
      this.debug("Navigated to fresh search, skipping listing")
    else
      listSearch(search.id, 50, isOffline())
  }

  onExpandedFolder = (folder: StaticFile) => {
    const { listMultipleFoldersWithViewModeProperties } = useFileSystem()
    const { setCurrentNavigationState, state: navigationState, currentOpenFolders, currentRootFolder } = useNavigation()
    const { addEntityEventListener } = useEvents()
    const { isOffline } = useCache()

    this.state.value.lastOpenedFolderId = folder.id

    // in column view, clicking on an already expanded folder still calls this callback, since
    // we have actions we want to take on expand events.
    if (currentOpenFolders.value[folder.id])
      return this.debug("Folder already expanded, skipping callback actions")

    if (navigationState.value.current?.navType !== "collection-folder")
      return this.warn("Navigation state unexpected, was this a refresh?")

    // in column view, expanding a folder means opening all of its parents all the way to
    // the root
    const rootFolderId = currentRootFolder.value?.id
    const folderPath = folder.path()?.map(f => f.id)
    if (!rootFolderId || !folderPath || !folderPath.includes(rootFolderId))
      throw new Error("No root folder or expanded folder not a child of root")
    const folders = [folder.id, ...folderPath.slice(0, folderPath.indexOf(rootFolderId) + 1)]

    setCurrentNavigationState({
      ...navigationState.value.current,
      openFolders: Object.fromEntries(folders.map(f => [f, true])),
    })
    addEntityEventListener("childCreated", folder.id, this.onChildCreated)
    addEntityEventListener("childProperties", folder.id, this.onChildProperties)
    addEntityEventListener("childDeleted", folder.id, this.onChildDeleted)
    listMultipleFoldersWithViewModeProperties([folder.id], 50, isOffline())
  }

  onShrunkFolder = (folder: StaticFile) => {
    const { setCurrentNavigationState, state: navigationState, currentOpenFolders } = useNavigation()
    const { removeEntityEventListener } = useEvents()
    const { state } = useCollectionView()

    if (!currentOpenFolders.value[folder.id])
      return this.error("Folder already shrunk?")

    if (navigationState.value.current?.navType !== "collection-folder")
      return this.warn("Navigation state unexpected, was this a refresh?")

    delete currentOpenFolders.value[folder.id]
    setCurrentNavigationState({
      ...navigationState.value.current,
      openFolders: { ...currentOpenFolders.value },
    })
    removeEntityEventListener("childCreated", folder.id, this.onChildCreated)
    removeEntityEventListener("childProperties", folder.id, this.onChildProperties)
    removeEntityEventListener("childDeleted", folder.id, this.onChildDeleted)
    setCurrentNavigationState({
      ...navigationState.value.current,
      openFolders: currentOpenFolders.value,
    })

    // This removes everything not in our openFolders object, not just this shrunk folder
    for (const file of Object.values(state.value.contents)) {
      const parentId = file.inferredParentId()
      if (!parentId || !currentOpenFolders.value[parentId])
        this.removeFileFromView(file.id)
    }
  }

  onUpdatedViewModeSettings = (
    newProperties: InterfaceViewModeSettings,
    oldProperties: InterfaceViewModeSettings,
  ) => {
    const { listMultipleFoldersWithViewModeProperties } = useFileSystem()
    const { isOffline } = useCache()
    const { currentRootFolder, currentOpenFolders } = useNavigation()

    if (!isDefined(currentRootFolder.value))
      return

    // if the user has added more properties to show, re-request info from DB
    const oldShowProperties = new Set(oldProperties.show.map(s => s.propertyId))
    const newShowProperties = new Set(newProperties.show.map(s => s.propertyId))

    let requestNewProperties = false
    for (const propertyId of newShowProperties) {
      if (oldShowProperties.has(propertyId))
        continue
      requestNewProperties = true
      break
    }

    // if the new properties is not a subset of old, then we have to re-request properties
    // from the DB
    if (requestNewProperties)
      listMultipleFoldersWithViewModeProperties(Object.keys(currentOpenFolders.value), 50, isOffline())
  }

  onFileCreated = async (event: FileEvent) => {
    const { isShowingProperty } = useViewMode()
    const { state: uploadState } = useJobs()
    const { emitEntityEvent } = useEvents()
    const { state: collectionViewState } = useCollectionView()
    const file = StaticFile.fromEvent(event)

    // If the user just created a new folder,
    // we want to focus on its name. However, we want
    // to exclude uploaded items from that.
    for (const job of Object.values(uploadState.value.jobs))
      if (isDefined(job.files[file.id]))
        return

    if (!file.isFolder())
      return

    // In tests we don't care about activating the default property edit or scrolling
    if (isTesting())
      return

    if (!collectionViewState.value.createdFolders[event.id])
      return this.warn("File created event for a folder that was not created in this view", file.id)

    if (!isShowingProperty(PropertyId.FILE_NAME))
      return this.warn("Can't focus on folder name since the property is not visible")

    // wait until the next tick since we need to wait for the
    // file to display
    await nextTick()

    this.scrollToFile(file.id)
    emitEntityEvent(
      "requestedPropertyEdit",
      file.id,
      { fileId: file.id, propertyId: PropertyId.FILE_NAME, location: "collection-view" },
      false,
    )
  }

  onFileProperties = (event: FileEvent) => {
    const { currentRootFolder, currentSearch } = useNavigation()
    const { currentOpenFolders } = useNavigation()
    const { state } = useCollectionView()

    const file = StaticFile.fromEvent(event)

    if (currentRootFolder.value) {
      const parentId = file.inferredParentId()
      // if this properties event has our parent id in it, we decide based on it
      if (parentId) {
        if (currentOpenFolders.value[parentId])
          this.addFileIfNew(file)
        else
          this.removeFileFromView(file.id)
      }
      // If we don't have the parent id, then we might have some other update, so we check
      // if the file is in our contents to add as an update
      else if (state.value.contents[file.id])
        this.addFileIfNew(file)

      else {
        this.error("Unknown file properties event", event)
        throw new Error("Unknown file properties event received")
      }
    }
    else if (currentSearch.value) {
    // for searches we only add properties of existing files
      if (state.value.contents[file.id])
        this.addFileIfNew(file)
      else {
        this.error("Unknown file properties event", event)
        throw new Error("Unknown file properties event received")
      }
    }
  }

  onFileDeleted = (event: FileEvent) => {
    this.removeFileFromView(event.id)
  }

  onChildCreated = (event: FileEvent) => {
    this.onFileCreated(event)
  }

  onChildProperties = (event: FileEvent) => {
    this.addFileIfNew(StaticFile.fromEvent(event))
  }

  onChildDeleted = (event: FileEvent) => {
    this.removeFileFromView(event.id)
  }

  onChildRemoved = (event: FileEvent) => {
    this.removeFileFromView(event.id)
  }

  onSearchResult = (event: FileEvent) => {
    const file = StaticFile.fromEvent(event)

    // We have to add the file to the view.
    // TODO: is there a way to make this search id specific?
    this.addFileIfNew(file)
  }

  onWentOnline = () => {
    const { listMultipleFoldersWithViewModeProperties } = useFileSystem()
    const { currentOpenFolders, currentRootFolder } = useNavigation()
    if (isDefined(currentRootFolder.value)) {
      this.info("Went online, listing folder again")
      listMultipleFoldersWithViewModeProperties(Object.keys(currentOpenFolders.value), 50, false)
    }
  }

  sortedContents = computed(() => {
    const { state, sortByViewModeSort } = useCollectionView()
    return sortByViewModeSort(Object.values(state.value.contents))
  })

  contentsByColumn = computed(() => {
    const { currentOpenFolders, currentRootFolder } = useNavigation()
    const openFolders = currentOpenFolders.value
    const rootFolder = currentRootFolder.value?.asStatic()

    if (!rootFolder)
      return []

    if (len(this.sortedContents.value) === 0) {
      return [{ files: [], folder: rootFolder }]
    }

    // find the file which has all of the open folders on its path
    let lowestFile
    for (const file of this.sortedContents.value) {
      let missing = len(openFolders)
      if (file.id in openFolders)
        missing -= 1
      for (const item of file.path() ?? [])
        if (item.id in openFolders)
          missing -= 1
      if (missing !== 0)
        continue
      lowestFile = file
      break
    }
    if (!lowestFile) {
      this.error("No lowest file found, cannot compute contents by column")
      return [{ files: [], folder: rootFolder }]
    }

    const contents: { files: StaticFile[], folder: StaticFile }[] = []
    for (const folder of [...(lowestFile?.path()?.reverse() ?? []), lowestFile])
      if (folder.id in openFolders)
        contents.push({ files: [], folder })

    // Now that we have our path, let's place our files in the right column. Note that we also
    // use the file contents file as the "folder" field, since we want the most complete file
    // to place in this field as possible. Taking a file from a path might result in a
    // broken or blank file. This is also why we add the root folder into the contents as well --
    // we want to have the most complete file possible so we don't run into "incomplete file"
    // errors

    for (const folderContents of contents)
      if (rootFolder.id === folderContents.folder.id)
        folderContents.folder = rootFolder

    for (const file of this.sortedContents.value) {
      for (const folderContents of contents) {
        if (file.id === folderContents.folder.id)
          folderContents.folder = file
        else if (file.inferredParentId() && file.inferredParentId() === folderContents.folder.id)
          folderContents.files.push(file)
      }
    }

    return contents
  })

  groupedContentsByColumn = computed(() => {
    const { groupByViewModeGroup } = useCollectionView()
    return this.contentsByColumn.value.map(({ files, folder }) => ({
      groups: groupByViewModeGroup(files),
      folder,
    }))
  })

  /** Gives the deepest folder (i.e. the "currently open" folder) */
  _deepestFolder = () => {
    const { currentOpenFolders, currentRootFolder } = useNavigation()
    const { state } = useCollectionView()
    const openFolders = currentOpenFolders.value
    const rootFolder = currentRootFolder.value

    if (!rootFolder)
      return

    // find the file which has all of the open folders on its path
    let lowestFile: StaticFile | undefined
    for (const file of this.sortedContents.value) {
      let missing = len(openFolders)
      if (file.id in openFolders)
        missing -= 1
      for (const item of file.path() ?? [])
        if (item.id in openFolders)
          missing -= 1
      if (missing !== 0)
        continue
      lowestFile = file
      break
    }
    if (!isDefined(lowestFile) || !lowestFile.inferredParentId())
      return

    // find the folder of the lowest file
    return state.value.contents[lowestFile.inferredParentId()!]
  }

  deepestFolder = computed(() => this._deepestFolder())

  /** Updates the bounding boxes of every file in the folder (can be time consuming!) */
  updateContentBoundsSync = () => {
    const { collectionScrollContainerId, fileIdAttr } = useGlobals()
    const { scrollX, scrollY } = useGestures().state.value.mainView
    const { state } = useCollectionView()

    state.value.contentBounds = {}

    const collectionView = document.getElementById(collectionScrollContainerId)
    if (!collectionView)
      return
    const query = dataAttrQuerySelector(fileIdAttr)
    collectionView.querySelectorAll<HTMLDivElement>(query).forEach((element) => {
      const fileId = element.dataset.fileId!
      if (!state.value.contents[fileId])
        return this.error("No such file in contents, somehow", fileId)
      const { x, y, left, right, top, bottom, ...rest } = element.getBoundingClientRect()
      state.value.contentBounds[fileId] = {
        ...rest,
        x: x + scrollX,
        y: y + scrollY,
        left: left + scrollX,
        right: right + scrollX,
        top: top + scrollY,
        bottom: bottom + scrollY,
      }
    })
  }

  scrollToFile = (fileId: string) => {
    const { state, updateFileContentBoundsSync } = useCollectionView()
    const { collectionScrollContainerId, collectionViewTopBarPadding } = useGlobals()

    updateFileContentBoundsSync()

    const bottomPadding = 4
    const topPadding = collectionViewTopBarPadding
    const fileLocation = state.value.contentBounds[fileId]
    if (!fileLocation)
      return this.error("Cannot scroll to nonexistent file")
    const container = document.getElementById(collectionScrollContainerId)
    if (!container)
      throw new Error("Cannot find container to scroll")
    const scrollTop = container.scrollTop
    const scrollBottom = scrollTop + container.clientHeight

    if (fileLocation.bottom > scrollBottom)
      container.scrollTo(0, fileLocation.bottom - container.clientHeight + bottomPadding)
    else if (fileLocation.top < scrollTop)
      container.scrollTo(0, fileLocation.top - topPadding)
  }

  onColumnViewMounted = () => {
    this.addListeners()
  }

  onColumnViewUnmounted = () => {
    this.clearListeners()
  }
}

const columnView = new ColumnView("column-view", theme.colors.purple.hex)
export default function () {
  return columnView.use()
}
